<script setup lang="ts">
import { useDebounceFn } from '@vueuse/core'

import axios from 'axios'
import { Loader2 } from 'lucide-vue-next'

import AudienceStoreSelectField from './AudienceStoreSelectField.vue'

import type { Audience } from '@/types/types'
import useContentScope from '@/composables/contentScope'
import type { ContentScope } from '@/types/contentScope'

const { createAudience, fetchMerchantAudiences, segmentTypes } = useAudience()
const { t } = useI18n()
const router = useRouter()

const props = withDefaults(
  defineProps<{
    isOpen: boolean
    segmentName?: string | null
  }>(),
  {
    isOpen: false,
    segmentName: ''
  }
)

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'update:segmentName', value: string): void
}>()

// AppRadioCard writes the full { value, disabled } option into `type` while the form
// is open; it's only normalized to a string (see handleCreateAudience) right before submit.
type AudienceTypeOption = { value: string; disabled?: boolean }
type SelectedAudience = Omit<Audience, 'type'> & { type: string | AudienceTypeOption }

const isTypeOption = (value: SelectedAudience['type']): value is AudienceTypeOption =>
  typeof value === 'object' && value !== null

const isCreating = ref(false)
const isCheckingName = ref(false)
const selectedAudience = ref<SelectedAudience>({
  name: '',
  type: '',
  store_ids: []
})
const audienceNames = ref([])
const { canChooseScope } = useContentScope()
const step = ref<1 | 2>(1)
const scope = ref<ContentScope | null>(null)
const hasTriedToCreate = ref(false)
const hasScopeError = computed(() => hasTriedToCreate.value && !scope.value)
const formErrors = ref(null)

const nextLabel = computed(() => {
  if (step.value !== 1) return ''
  return canChooseScope.value ? t('global.words.next') : createModalProps.linkText
})

const createModalProps = {
  headingTitle: t('pages.customer.audiences.header.createBtn'),
  linkText: t('pages.customer.audiences.create.createBtn'),
  cancel: t('global.words.cancel')
}

const mappedTypes = computed(() => {
  return segmentTypes
    .filter((type) => type.id !== 'typology')
    .map((type) => {
      return (type = {
        value: type.id,
        disabled: type.disabled
      })
    })
})

let cancelTokenSource = null

const searchAudienceByName = async (audienceName) => {
  if (cancelTokenSource) {
    cancelTokenSource.cancel('Operation canceled by the user.')
  }

  cancelTokenSource = axios.CancelToken.source()

  try {
    const audiencesData = await fetchMerchantAudiences({
      search: audienceName,
      cancelToken: cancelTokenSource.token
    })

    audienceNames.value = audiencesData.data?.map((audience) => audience.name)
  } catch (error) {
    if (axios.isCancel(error)) {
      throw new Error(`Search canceled: ${error.message}`)
    } else {
      throw error
    }
  } finally {
    cancelTokenSource = null
  }
}

interface Rule {
  fieldName: string
  validation: (value: any) => boolean
  errorMessage: string
}

const rules: Rule[] = [
  {
    fieldName: 'name',
    validation: (value) => value !== '',
    errorMessage: t('pages.customer.audiences.create.error.name')
  },
  {
    fieldName: 'duplicate',
    validation: () =>
      audienceNames.value.every(
        (name) => name.toLowerCase().trim() !== selectedAudience.value.name.toLowerCase().trim()
      ),
    errorMessage: t('pages.customer.audiences.create.error.duplicate')
  },
  {
    fieldName: 'type',
    validation: (value) => value.value === 'static' || value.value === 'dynamic',
    errorMessage: t('pages.customer.audiences.create.error.type')
  }
]

const validateForm = (audience, rules: Rule[]) => {
  const errors = []
  rules.forEach((rule) => {
    if (!rule.validation(audience[rule.fieldName])) {
      errors.push(rule)
    }
  })
  return errors
}

const getFieldErrorMessage = (fieldname) => {
  return formErrors.value.filter((error) => error.fieldName === fieldname).map((error) => error.errorMessage)
}

const isFormValid = (fields = null) => {
  const filteredRules = rules.filter((rule) => fields?.includes(rule.fieldName))
  formErrors.value = validateForm(selectedAudience.value, fields ? filteredRules : rules)
  return formErrors.value.length === 0
}

const checkNameError = async (name) => {
  try {
    await searchAudienceByName(name)
    isFormValid(['name', 'duplicate'])
  } finally {
    isCheckingName.value = false
  }
}

const debouncedCheckNameError = useDebounceFn(checkNameError, 500)

const handleNameInput = (name) => {
  if (name.length >= 1) {
    isCheckingName.value = true
  }
  debouncedCheckNameError(name)
}

const handleNext = () => {
  if (!isFormValid()) return
  if (canChooseScope.value && step.value === 1) {
    step.value = 2
    return
  }
  handleCreateAudience()
}

const handleCreateAudience = async () => {
  if (!canChooseScope.value) scope.value = 'local'
  if (!scope.value) {
    hasTriedToCreate.value = true
    return
  }
  isCreating.value = true
  if (!isFormValid()) {
    isCreating.value = false
    return
  }

  if (isTypeOption(selectedAudience.value.type) && selectedAudience.value.type.value === 'dynamic') {
    return router.push({ name: 'AudienceCreate', query: { name: selectedAudience.value.name, scope: scope.value } })
  }

  try {
    const audiencePayload: Audience = {
      ...selectedAudience.value,
      type: isTypeOption(selectedAudience.value.type) ? selectedAudience.value.type.value : selectedAudience.value.type,
      scope: scope.value
    }
    const newAudienceId = await createAudience({ audience: audiencePayload })
    if (newAudienceId) {
      router.push({ name: 'AudienceDetail', params: { audienceId: newAudienceId } })
    }
  } catch (error) {
    return error
  } finally {
    isCreating.value = false
  }
}

const isStaticType = computed(
  () => isTypeOption(selectedAudience.value.type) && selectedAudience.value.type.value === 'static'
)

const closeModal = () => {
  selectedAudience.value = { name: '', type: '', store_ids: [] }
  formErrors.value = null
  emit('close')
}

const getNameErrors = computed(() => {
  if (!formErrors.value) {
    return []
  }
  if (formErrors.value.some((error) => error.fieldName === 'name')) {
    return getFieldErrorMessage('name')
  }
  if (formErrors.value.some((error) => error.fieldName === 'duplicate')) {
    return getFieldErrorMessage('duplicate')
  }
  return []
})

watch(
  () => props.isOpen,
  (newValue) => {
    if (newValue) {
      selectedAudience.value = { name: '', type: '', store_ids: [] }
      step.value = 1
      scope.value = null
      hasTriedToCreate.value = false
      formErrors.value = null
    }
  }
)
</script>

<template>
  <BaseModal
    v-if="selectedAudience"
    size="2xl"
    :is-open="isOpen"
    :heading-title="step === 2 ? t('contentScope.subjects.audience.fieldTitle') : createModalProps.headingTitle"
    :cancel="step === 1 ? createModalProps.cancel : ''"
    :link-text="nextLabel"
    :is-processing="isCreating || isCheckingName"
    @close="closeModal"
    @click="handleNext"
  >
    <ContentScopeQuestion
      v-if="step === 2"
      v-model="scope"
      subject="audience"
      :error="hasScopeError"
      :with-label="false"
    />
    <div v-else class="space-y-5 text-foreground">
      <AppFormInput
        id="audienceName"
        v-model="selectedAudience.name"
        class="space-y-1.5 text-left"
        :label="t('pages.customer.audiences.create.audienceName')"
        :errors="getNameErrors"
        autoFocus="true"
        maxlength="50"
        @update:model-value="handleNameInput"
      />
      <fieldset class="space-y-1.5">
        <legend class="inline-flex w-full text-left text-sm font-medium">
          {{ t('pages.customer.audiences.create.audienceType') }}
        </legend>
        <template v-if="formErrors">
          <span v-if="getFieldErrorMessage('type')[0]" class="mt-2 inline-flex w-full text-left text-sm text-red-600">{{
            getFieldErrorMessage('type')[0]
          }}</span>
        </template>
        <AppRadioCard
          id="audienceType"
          v-model="selectedAudience.type"
          class="grid gap-4 sm:grid-cols-2"
          :options="mappedTypes"
        >
          <template v-for="(_, index) in mappedTypes" :key="index" #[index]>
            <BaseChip
              class="mb-2"
              :color="segmentTypes[index].color"
              :icon="segmentTypes[index]?.icon"
              :text="segmentTypes[index]?.text"
            />
            <p class="text-left text-sm text-foreground">
              {{ segmentTypes[index]?.description }}
            </p>
          </template>
        </AppRadioCard>
      </fieldset>
      <AudienceStoreSelectField v-if="isStaticType" v-model="selectedAudience.store_ids" />
    </div>
    <template v-if="step === 2" #linkBtn>
      <div class="flex w-full items-center justify-end gap-4">
        <Button variant="outline" :disabled="isCreating" @click="step = 1">
          {{ t('global.words.back') }}
        </Button>
        <Button variant="default-primary" :disabled="isCreating" @click="handleCreateAudience">
          <Loader2 v-if="isCreating" class="animate-spin" />
          {{ createModalProps.linkText }}
        </Button>
      </div>
    </template>
  </BaseModal>
</template>
